import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import {
    DEPRECATED_ORANGE_CALENDAR_COLOR,
    ORANGE_CALENDAR_COLOR,
    generateColorScheme,
} from 'owa-color-utils/lib/colorUtils';
import {
    getCalendarEntry,
    getCalendarIdByFolderId,
    CalendarId,
    getCalendarEntryByFolderId,
} from 'owa-calendar-cache';
import { getCategoryColorsForCategory, getMasterCategoryList } from 'owa-categories';
import { trace } from 'owa-trace';
import {
    getColorSchemeForCalendarColor,
    releaseCalendarColor,
    getDefaultColorSchemes,
} from './CalendarColorSchemes';

/**
 * Cache of calendars assigned with colors
 */
let assignedCalendars: { [key: string]: ColorScheme } = {};

/**
 * The properties that need to be present on an event in order to get it's color scheme
 */
export interface ColorableEvent {
    ParentFolderId?: {
        Id: string;
    };
    Categories?: string[];
}

/**
 * Gets calendar color scheme by folder id
 * @returns the color scheme for the calendar
 */
export function getCalendarColorSchemeByFolderId(calendarFolderId: string): ColorScheme {
    let calendarId = getCalendarIdByFolderId(calendarFolderId);

    if (calendarId) {
        return getCalendarColorScheme(calendarId);
    } else {
        trace.warn(
            '[getCalendarColorSchemeByFolderId] Failed to retrieve the calendar Id while choosing color scheme'
        );
        return getColorSchemeForCalendarColor(CalendarColor.LightBlue);
    }
}

/**
 * Gets calendar color scheme for the surface item model. Uses the category color if item is categorized, otherwise uses the
 * parent folderId to grab the calendar color.
 * @returns the color scheme for the item
 */
export function getCalendarEventColorScheme(model: ColorableEvent): ColorScheme {
    let categories = model && model.Categories;
    if (!model.ParentFolderId) {
        trace.warn('[getCalendarEventColorScheme] Calendar item does not have parent folder id');
        return getColorSchemeForCalendarColor(CalendarColor.LightBlue);
    }
    return getColorScheme(model.ParentFolderId.Id, categories);
}

/**
 * Gets calendar color scheme by calendar id
 * @param calendarId The calendar id to retrieve the color for
 * @returns The color scheme for the calendar
 */
export function getCalendarColorScheme(calendarId: CalendarId): ColorScheme {
    let assignedCalendarColors = assignedCalendars[calendarId.id];
    if (!assignedCalendarColors) {
        assignedCalendarColors = refreshAssignedColorScheme(calendarId);
    }
    return assignedCalendarColors;
}

/** Refreshes the assigned color of a calendar based on the calendar color in the calendarsCache
 * @param calendarId id of calendar entry to refresh
 * @returns the color assigned to the calendar entry
 */
export function refreshAssignedColorScheme(calendarId: CalendarId): ColorScheme {
    const calendarEntry = getCalendarEntry({ id: calendarId });

    if (assignedCalendars[calendarId.id] && calendarEntry.CalendarColor === -1) {
        // if the color has already been assigned and is automatic, release that color
        releaseCalendarColor(assignedCalendars[calendarId.id].calPrimary);
    }

    let assignedCalendarColors;
    if (
        calendarEntry &&
        calendarEntry.HexColorString &&
        calendarEntry.HexColorString != '' &&
        getDefaultColorSchemes().some(
            color => color.calPrimary.toLowerCase() == calendarEntry.HexColorString.toLowerCase()
        )
    ) {
        assignedCalendarColors = generateColorScheme(calendarEntry.HexColorString);
    } else if (
        calendarEntry &&
        calendarEntry.HexColorString &&
        calendarEntry.HexColorString != '' &&
        DEPRECATED_ORANGE_CALENDAR_COLOR.toLowerCase() == calendarEntry.HexColorString.toLowerCase()
    ) {
        // this shim is necessary to gracefully migrate users from deprecated orange to A11Y-compliant orange
        // since we don't want to fall back to the CalendarColor enum associated with deprecated orange
        // in backend storage (i.e. CalendarColor.Red = 2), which is a regression from user perspective
        assignedCalendarColors = generateColorScheme(ORANGE_CALENDAR_COLOR);
    } else {
        assignedCalendarColors = getColorSchemeForCalendarColor(calendarEntry.CalendarColor);
    }

    assignedCalendars[calendarId.id] = assignedCalendarColors;

    return assignedCalendarColors;
}

function getColorScheme(containerId: string, categories: string[]) {
    // the color scheme for the item is dependent on the categories for the item
    let colorScheme = getCalendarColorSchemeByFolderId(containerId);
    if (categories && categories.length > 0) {
        let categoryList = getMasterCategoryList();
        let calendarEntry = getCalendarEntryByFolderId(containerId);
        if (calendarEntry) {
            if ((calendarEntry as LocalCacheForRemoteCalendarEntry).RemoteCategories) {
                categoryList = (calendarEntry as LocalCacheForRemoteCalendarEntry).RemoteCategories
                    .MasterList;
            }

            let categoryColor = getCategoryColorsForCategory(categories[0], categoryList);
            colorScheme = generateColorScheme(categoryColor.iconColor);
        }
    }

    return colorScheme;
}
